import { backgroundOrange, darkOrange, green, orange } from '@/theme/colors'
import styled from 'styled-components'

export const SectionRoot = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
`

export const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	padding-bottom: 0.875rem;
	border-bottom: 1px solid #f0e0d6;
	margin-bottom: 0.875rem;
	flex-shrink: 0;
	flex-wrap: wrap;
`

export const SectionTitle = styled.h3`
	margin: 0;
	font-size: 1.0625rem;
	font-weight: 700;
	color: ${darkOrange};
`

export const SectionBody = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding-right: 0.25rem;
	display: flex;
	flex-direction: column;
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
	}
`

export const MemberSectionList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`

export const InlineSummaryCard = styled.div<{
	$tone: 'settled' | 'owe' | 'owed' | 'mixed'
}>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	background: ${({ $tone }) => {
		if ($tone === 'settled') return '#e8f5ee'
		if ($tone === 'owe') return '#fee2e2'
		if ($tone === 'owed') return '#dcfce7'
		return 'white'
	}};
	border: 1px solid
		${({ $tone }) => {
			if ($tone === 'settled') return '#bfe5ce'
			if ($tone === 'owe') return '#fecaca'
			if ($tone === 'owed') return '#bbf7d0'
			return '#f0e0d6'
		}};
	border-radius: 10px;
	padding: 0.625rem 0.875rem;
	margin-bottom: 0.875rem;
	flex-shrink: 0;
`

export const InlineSummaryLabel = styled.span`
	font-size: 0.75rem;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
`

export const InlineSummaryValue = styled.span<{
	$tone: 'settled' | 'owe' | 'owed' | 'mixed'
}>`
	font-size: 1rem;
	font-weight: 700;
	color: ${({ $tone }) => {
		if ($tone === 'settled') return green
		if ($tone === 'owe') return '#dc2626'
		if ($tone === 'owed') return green
		return '#262626'
	}};
	text-align: right;
`

export const InlineSettledNote = styled.span`
	font-size: 0.8125rem;
	font-weight: 600;
	color: ${green};
	display: block;
	margin-bottom: 0.875rem;
	text-align: center;
	flex-shrink: 0;
`

export const HeaderActionButton = styled.button`
	background: ${orange};
	color: white;
	border: none;
	padding: 0.5rem 0.875rem;
	border-radius: 8px;
	font-size: 0.8125rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	white-space: nowrap;

	&:hover {
		opacity: 0.9;
		transform: translateY(-1px);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	}

	&:active {
		opacity: 0.85;
		transform: translateY(0);
	}
`

export const MemberSectionWrapper = styled.div`
	background: white;
	border-radius: 12px;
	border: 1px solid #f0e0d6;
	overflow: hidden;
`

export const MemberHeaderRow = styled.button<{ $clickable: boolean }>`
	width: 100%;
	background: white;
	border: none;
	padding: 0.875rem 1.125rem;
	display: flex;
	align-items: center;
	gap: 0.75rem;
	text-align: left;
	cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
	transition: background 0.15s ease;
	flex-wrap: wrap;

	&:hover {
		background: ${({ $clickable }) => ($clickable ? '#fffaf6' : 'white')};
		transform: none;
	}

	@media (max-width: 600px) {
		padding: 0.75rem 0.875rem;
		gap: 0.5rem;
	}
`

export const MemberAvatar = styled.div<{ $size?: 'sm' | 'md' }>`
	width: ${({ $size }) => ($size === 'sm' ? '20px' : '32px')};
	height: ${({ $size }) => ($size === 'sm' ? '20px' : '32px')};
	border-radius: 50%;
	background: ${orange};
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 600;
	font-size: ${({ $size }) => ($size === 'sm' ? '9px' : '12px')};
	flex-shrink: 0;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`

export const MemberHeaderMain = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
	flex: 1;
	min-width: 0;
`

export const MemberHeadline = styled.div`
	font-size: 0.9375rem;
	color: #262626;
	line-height: 1.4;
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
	min-width: 0;
`

export const MemberHeadlineLine = styled.span`
	display: block;
	overflow-wrap: anywhere;
	word-break: break-word;
`

export const MemberHeadlineName = styled.span`
	font-weight: 700;
	color: #262626;
	display: inline-block;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: bottom;
`

export const MemberHeadlineAmount = styled.span<{ $tone: 'owe' | 'owed' }>`
	font-weight: 700;
	color: ${({ $tone }) => ($tone === 'owe' ? '#dc2626' : green)};
`

export const ExpandChevron = styled.span<{ $expanded: boolean }>`
	color: ${darkOrange};
	font-size: 1rem;
	flex-shrink: 0;
	transition: transform 0.15s ease;
	transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
`

export const BreakdownContainer = styled.div`
	background: ${backgroundOrange};
	padding: 0.75rem 1.125rem 1rem 1.125rem;
	border-top: 1px solid #f5ebe5;
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`

export const BreakdownRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
	background: white;
	border-radius: 10px;
	padding: 0.75rem 0.875rem;
	border: 1px solid #f0e0d6;
	min-width: 0;
`

export const BreakdownSentence = styled.div`
	font-size: 0.875rem;
	color: #262626;
	line-height: 1.4;
`

export const BreakdownSentenceAmount = styled.span<{ $tone: 'owe' | 'owed' }>`
	font-weight: 700;
	color: ${({ $tone }) => ($tone === 'owe' ? '#dc2626' : green)};
`

export const RecordPayUpButton = styled.button`
	align-self: flex-start;
	background: ${orange};
	color: white;
	border: none;
	padding: 0.4rem 0.875rem;
	border-radius: 8px;
	font-size: 0.8125rem;
	font-weight: 600;
	cursor: pointer;
	transition: opacity 0.15s ease;

	&:hover {
		opacity: 0.9;
	}
`
